// CAD-style view cube (top-right): tracks main camera orientation; faces,
// edges and corners set a standard view with a short animated tween.
import * as THREE from 'three';
import { cameraDistance } from './camera-rig';
import { configureRenderer } from './three-setup';
import { dom } from './ui';
import { chromeScale, onChromeScaleChange } from './ui-scale';
import {
  camera,
  fitCameraToModel,
  isDark,
  keepRenderingWhile,
  onAfterRender,
  onFrame,
  onThemeChange,
  pivot,
  requestRender,
  updateCameraClipping
} from './viewer';
import { connectWamp, isWampConnected, wampTeardown } from './wamp';

const CSS_SIZE_BASE = 132;

function cubeCssSize(): number {
  return Math.round(CSS_SIZE_BASE * chromeScale());
}
const HALF = 0.5;
const ANIM_MS = 380;

type PickKind = 'face' | 'edge' | 'corner';

interface PickTarget {
  kind: PickKind;
  /** Unit direction from pivot toward the camera for this view. */
  dir: THREE.Vector3;
  mesh: THREE.Mesh;
  label?: string;
}

const cubeScene = new THREE.Scene();
const cubeCamera = new THREE.PerspectiveCamera(32, 1, 0.1, 20);
cubeCamera.position.set(0, 0, 4.0);
cubeCamera.lookAt(0, 0, 0);

const root = new THREE.Group();
cubeScene.add(root);

const faceGroup = new THREE.Group();
const pickGroup = new THREE.Group();
const highlightMat = new THREE.MeshBasicMaterial({
  toneMapped: false,
  color: 0x0070e0,
  transparent: true,
  opacity: 0.28,
  depthTest: true,
  depthWrite: false
});
const highlightMesh = new THREE.Mesh(new THREE.BufferGeometry(), highlightMat);
highlightMesh.visible = false;
root.add(faceGroup, pickGroup, highlightMesh);

/** Glass look: clear face centres, frosted chamfer bands (edge/corner click zones). */
interface CubePalette {
  band: string;
  centre: string;
  edge: string;
  bandLine: string;
  ink: string;
  highlight: number;
}

function palette(dark: boolean): CubePalette {
  return dark
    ? {
        band: 'rgba(255,255,255,0.22)',
        centre: 'rgba(255,255,255,0.08)',
        edge: 'rgba(255,255,255,0.5)',
        bandLine: 'rgba(255,255,255,0.22)',
        ink: '#e8e6e1',
        highlight: 0x3399ff
      }
    : {
        band: 'rgba(206,212,220,0.72)',
        centre: 'rgba(255,255,255,0.46)',
        edge: 'rgba(27,30,36,0.5)',
        bandLine: 'rgba(27,30,36,0.24)',
        ink: '#1b1e24',
        highlight: 0x0070e0
      };
}

const picks: PickTarget[] = [];
let cubeRenderer: THREE.WebGLRenderer | null = null;
let registered = false;
let hoverPick: PickTarget | null = null;

const _raycaster = new THREE.Raycaster();
const _ndc = new THREE.Vector2();
const _viewDir = new THREE.Vector3();
const _up = new THREE.Vector3();
const _endPos = new THREE.Vector3();
const _lookMat = new THREE.Matrix4();
const _qTmp = new THREE.Quaternion();
const _localY = new THREE.Vector3();
const _projUp = new THREE.Vector3();
const _qAlign = new THREE.Quaternion();

let animating = false;
let animStart = 0;
let animFromPos = new THREE.Vector3();
let animToPos = new THREE.Vector3();
let animFromQuat = new THREE.Quaternion();
let animToQuat = new THREE.Quaternion();
let animReconnectWamp = false;

/** Glass face: frosted band around a clearer centre; label only on outward faces. */
function faceTexture(label: string | null, p: CubePalette): THREE.CanvasTexture {
  const size = 512;
  const band = 62;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);

  ctx.fillStyle = p.band;
  ctx.fillRect(0, 0, size, size);
  ctx.clearRect(band, band, size - band * 2, size - band * 2);
  ctx.fillStyle = p.centre;
  ctx.fillRect(band, band, size - band * 2, size - band * 2);

  ctx.strokeStyle = p.bandLine;
  ctx.lineWidth = 5;
  ctx.strokeRect(band, band, size - band * 2, size - band * 2);
  ctx.strokeStyle = p.edge;
  ctx.lineWidth = 10;
  ctx.strokeRect(5, 5, size - 10, size - 10);

  if (label) {
    ctx.fillStyle = p.ink;
    const px = label.length >= 6 ? 84 : 96;
    ctx.font = `600 ${px}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, size / 2, size / 2 + 3);
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.anisotropy = 8;
  return tex;
}

const FACE_SIZE = 0.94;

function orientFace(mesh: THREE.Object3D, dir: THREE.Vector3, upHint: THREE.Vector3): void {
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir);
  _projUp.copy(upHint).addScaledVector(dir, -upHint.dot(dir));
  if (_projUp.lengthSq() > 1e-8) {
    _projUp.normalize();
    _localY.set(0, 1, 0).applyQuaternion(mesh.quaternion);
    _qAlign.setFromUnitVectors(_localY, _projUp);
    mesh.quaternion.premultiply(_qAlign);
  }
}

function makeFace(
  label: string,
  dir: THREE.Vector3,
  upHint: THREE.Vector3,
  p: CubePalette
): void {
  // Outward side carries the label; the inward side (seen through the glass
  // from the opposite face) is the same glass without text, so nothing reads mirrored.
  const mat = new THREE.MeshBasicMaterial({
    toneMapped: false,
    map: faceTexture(label, p), transparent: true, depthWrite: false, side: THREE.FrontSide
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
  orientFace(mesh, dir, upHint);
  mesh.position.copy(dir).multiplyScalar(HALF);
  mesh.renderOrder = 2;
  faceGroup.add(mesh);

  const backMat = new THREE.MeshBasicMaterial({
    toneMapped: false,
    map: faceTexture(null, p), transparent: true, depthWrite: false, side: THREE.BackSide
  });
  const back = new THREE.Mesh(mesh.geometry, backMat);
  back.quaternion.copy(mesh.quaternion);
  back.position.copy(mesh.position);
  back.renderOrder = 1;
  faceGroup.add(back);

  const pick = new THREE.Mesh(
    new THREE.PlaneGeometry(FACE_SIZE * 0.98, FACE_SIZE * 0.98),
    invisiblePickMat(THREE.DoubleSide)
  );
  pick.quaternion.copy(mesh.quaternion);
  pick.position.copy(dir).multiplyScalar(HALF + 0.02);
  pickGroup.add(pick);
  picks.push({ kind: 'face', dir: dir.clone(), mesh: pick, label });
}

/** Invisible pick only — no decorative rails/chips (keeps the cube calm). */
function addEdge(a: THREE.Vector3, b: THREE.Vector3): void {
  const mid = a.clone().add(b).multiplyScalar(0.5);
  const dir = mid.clone().normalize();
  const axis = b.clone().sub(a);
  const len = Math.max(axis.length() - 0.1, 0.2);
  axis.normalize();

  const pick = new THREE.Mesh(
    new THREE.BoxGeometry(0.12, 0.12, len + 0.06),
    invisiblePickMat()
  );
  pick.position.copy(mid);
  pick.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), axis);
  pickGroup.add(pick);
  picks.push({ kind: 'edge', dir, mesh: pick });
}

function addCorner(pos: THREE.Vector3): void {
  const dir = pos.clone().normalize();
  const pick = new THREE.Mesh(
    new THREE.BoxGeometry(0.2, 0.2, 0.2),
    invisiblePickMat()
  );
  pick.position.copy(pos);
  pickGroup.add(pick);
  picks.push({ kind: 'corner', dir, mesh: pick });
}

function disposeMaterial(m: THREE.Material): void {
  const mapped = m as THREE.MeshBasicMaterial;
  if (mapped.map) mapped.map.dispose();
  m.dispose();
}

function disposeObject(obj: THREE.Object3D): void {
  const mesh = obj as THREE.Mesh | THREE.Line;
  if (mesh.geometry) mesh.geometry.dispose();
  const mat = (mesh as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
  if (Array.isArray(mat)) mat.forEach(disposeMaterial);
  else if (mat) disposeMaterial(mat);
}

function invisiblePickMat(side: THREE.Side = THREE.FrontSide): THREE.MeshBasicMaterial {
  // visible:false meshes are skipped by Raycaster — keep mesh visible, hide via material.
  return new THREE.MeshBasicMaterial({
    toneMapped: false,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    side
  });
}

function clearBuilt(): void {
  for (let i = faceGroup.children.length - 1; i >= 0; i--) {
    const obj = faceGroup.children[i]!;
    faceGroup.remove(obj);
    disposeObject(obj);
  }
  for (let i = pickGroup.children.length - 1; i >= 0; i--) {
    const obj = pickGroup.children[i]!;
    pickGroup.remove(obj);
    disposeObject(obj);
  }
  picks.length = 0;
}

function buildCube(): void {
  clearBuilt();
  const p = palette(isDark());
  highlightMat.color.set(p.highlight);
  highlightMat.opacity = 0.22;

  makeFace('FRONT', new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 1, 0), p);
  makeFace('BACK', new THREE.Vector3(0, 0, -1), new THREE.Vector3(0, 1, 0), p);
  makeFace('RIGHT', new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 1, 0), p);
  makeFace('LEFT', new THREE.Vector3(-1, 0, 0), new THREE.Vector3(0, 1, 0), p);
  makeFace('TOP', new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, -1), p);
  makeFace('BOTTOM', new THREE.Vector3(0, -1, 0), new THREE.Vector3(0, 0, 1), p);


  const s = HALF;
  const corners = [
    [1, 1, 1], [1, 1, -1], [1, -1, 1], [1, -1, -1],
    [-1, 1, 1], [-1, 1, -1], [-1, -1, 1], [-1, -1, -1]
  ] as const;
  for (const [x, y, z] of corners) {
    addCorner(new THREE.Vector3(x * s, y * s, z * s));
  }

  const edgeDefs: Array<[THREE.Vector3, THREE.Vector3]> = [];
  for (const sx of [-s, s]) {
    for (const sy of [-s, s]) {
      edgeDefs.push([new THREE.Vector3(sx, sy, -s), new THREE.Vector3(sx, sy, s)]);
    }
  }
  for (const sx of [-s, s]) {
    for (const sz of [-s, s]) {
      edgeDefs.push([new THREE.Vector3(sx, -s, sz), new THREE.Vector3(sx, s, sz)]);
    }
  }
  for (const sy of [-s, s]) {
    for (const sz of [-s, s]) {
      edgeDefs.push([new THREE.Vector3(-s, sy, sz), new THREE.Vector3(s, sy, sz)]);
    }
  }
  for (const [a, b] of edgeDefs) addEdge(a, b);
}

function upForDir(dir: THREE.Vector3): THREE.Vector3 {
  _up.set(0, 1, 0);
  if (Math.abs(dir.dot(_up)) > 0.99) {
    return dir.y > 0 ? new THREE.Vector3(0, 0, -1) : new THREE.Vector3(0, 0, 1);
  }
  return _up.addScaledVector(dir, -_up.dot(dir)).normalize();
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function beginViewAnim(dir: THREE.Vector3): void {
  _viewDir.copy(dir).normalize();
  const dist = cameraDistance();
  _endPos.copy(pivot).addScaledVector(_viewDir, dist);
  _lookMat.lookAt(_endPos, pivot, upForDir(_viewDir));
  animToQuat.setFromRotationMatrix(_lookMat);

  animFromPos.copy(camera.position);
  animFromQuat.copy(camera.quaternion);
  animToPos.copy(_endPos);

  // Abrupt camera jumps can upset 3DxNLServer — same pattern as Home.
  animReconnectWamp = isWampConnected();
  if (animReconnectWamp) wampTeardown(false);

  animStart = performance.now();
  animating = true;
  requestRender();
}

function tickAnim(now: number): void {
  if (!animating) return;
  const t = easeOutCubic(Math.min(1, (now - animStart) / ANIM_MS));
  camera.position.lerpVectors(animFromPos, animToPos, t);
  _qTmp.copy(animFromQuat).slerp(animToQuat, t);
  camera.quaternion.copy(_qTmp);
  updateCameraClipping();

  if (t >= 1) {
    animating = false;
    camera.position.copy(animToPos);
    camera.quaternion.copy(animToQuat);
    updateCameraClipping();
    if (animReconnectWamp) {
      animReconnectWamp = false;
      setTimeout(() => void connectWamp(), 400);
    }
  }
}

function setHover(pick: PickTarget | null): void {
  if (hoverPick === pick) return;
  hoverPick = pick;
  if (!pick) {
    highlightMesh.visible = false;
    dom.viewCubeCanvas.style.cursor = 'default';
    dom.viewCubeHost.title = 'View cube';
    paint();
    return;
  }

  const outward = pick.dir.clone().normalize();
  if (pick.kind === 'face') {
    highlightMesh.geometry.dispose();
    highlightMesh.geometry = new THREE.PlaneGeometry(FACE_SIZE * 0.96, FACE_SIZE * 0.96);
    highlightMesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), outward);
    highlightMesh.position.copy(outward).multiplyScalar(HALF + 0.03);
    highlightMesh.scale.set(1, 1, 1);
  } else if (pick.kind === 'edge') {
    highlightMesh.geometry.dispose();
    highlightMesh.geometry = new THREE.BoxGeometry(0.1, 0.1, 0.86);
    // Edge midpoints have one near-zero axis — that axis runs along the edge.
    const along = new THREE.Vector3(
      Math.abs(outward.x) < 0.15 ? 1 : 0,
      Math.abs(outward.y) < 0.15 ? 1 : 0,
      Math.abs(outward.z) < 0.15 ? 1 : 0
    );
    if (along.lengthSq() < 1e-6) along.set(0, 1, 0);
    along.normalize();
    highlightMesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), along);
    highlightMesh.position.copy(outward).multiplyScalar(Math.SQRT1_2);
    highlightMesh.scale.set(1, 1, 1);
  } else {
    highlightMesh.geometry.dispose();
    highlightMesh.geometry = new THREE.BoxGeometry(0.22, 0.22, 0.22);
    highlightMesh.quaternion.identity();
    highlightMesh.position.copy(outward).multiplyScalar(Math.sqrt(3) * HALF);
    highlightMesh.scale.set(1, 1, 1);
  }

  highlightMesh.visible = true;
  dom.viewCubeCanvas.style.cursor = 'pointer';
  const tip =
    pick.kind === 'face' && pick.label
      ? `${pick.label} view`
      : pick.kind === 'edge'
        ? 'Edge view'
        : 'Corner view';
  dom.viewCubeHost.title = tip;
  paint();
}

function pickAt(clientX: number, clientY: number): PickTarget | null {
  const rect = dom.viewCubeCanvas.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) return null;
  root.quaternion.copy(camera.quaternion).invert();
  root.updateMatrixWorld(true);
  _ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
  _ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
  _raycaster.setFromCamera(_ndc, cubeCamera);
  // Root is oriented with inverted main camera — pick in that space.
  const hits = _raycaster.intersectObjects(pickGroup.children, false);
  if (!hits.length) return null;

  // Prefer corners, then edges, then faces when depths are close.
  const rank = (k: PickKind): number => (k === 'corner' ? 0 : k === 'edge' ? 1 : 2);
  let best: { pick: PickTarget; dist: number; rank: number } | null = null;
  for (const hit of hits) {
    const pick = picks.find(p => p.mesh === hit.object);
    if (!pick) continue;
    const r = rank(pick.kind);
    if (
      !best ||
      r < best.rank ||
      (r === best.rank && hit.distance < best.dist)
    ) {
      best = { pick, dist: hit.distance, rank: r };
    }
  }
  return best?.pick ?? null;
}

const CUBE_CORNERS = [
  [1, 1, 1], [1, 1, -1], [1, -1, 1], [1, -1, -1],
  [-1, 1, 1], [-1, 1, -1], [-1, -1, 1], [-1, -1, -1]
].map(([x, y, z]) => new THREE.Vector3(x * HALF, y * HALF, z * HALF));
const _corner = new THREE.Vector3();

/** Convex hull (monotone chain) of 2D points, counter-clockwise. */
function convexHull(points: Array<[number, number]>): Array<[number, number]> {
  const pts = points.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o: number[], a: number[], b: number[]) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Array<[number, number]> = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: Array<[number, number]> = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

/**
 * WebGL cannot blur the page behind its own canvas, so a CSS backdrop-filter
 * layer sits under the cube canvas, clipped to the cube's projected outline.
 */
function syncBackdropBlur(): void {
  const projected = CUBE_CORNERS.map(c => {
    _corner.copy(c).applyQuaternion(root.quaternion).project(cubeCamera);
    return [(_corner.x + 1) * 50, (1 - _corner.y) * 50] as [number, number];
  });
  const hull = convexHull(projected);
  dom.viewCubeBlur.style.clipPath =
    'polygon(' + hull.map(([x, y]) => `${x.toFixed(2)}% ${y.toFixed(2)}%`).join(', ') + ')';
}

function paint(): void {
  if (!cubeRenderer) return;
  root.quaternion.copy(camera.quaternion).invert();
  cubeRenderer.render(cubeScene, cubeCamera);
  syncBackdropBlur();
}

function onPointerMove(e: PointerEvent): void {
  setHover(pickAt(e.clientX, e.clientY));
}

function onPointerLeave(): void {
  setHover(null);
}

function onPointerDown(e: PointerEvent): void {
  if (e.button !== 0) return;
  e.preventDefault();
  e.stopPropagation();
  const pick = pickAt(e.clientX, e.clientY);
  if (!pick) return;
  beginViewAnim(pick.dir);
}

/** Animate to a standard view looking from `dir` (e.g. +Z = front). */
export function animateToView(dir: THREE.Vector3): void {
  beginViewAnim(dir);
}

/** Frame the whole model (same as the Home button). */
export function goHome(): void {
  const was = isWampConnected();
  if (was) wampTeardown(false);
  fitCameraToModel();
  if (was) setTimeout(() => void connectWamp(), 400);
}

function onHomeClick(e: MouseEvent): void {
  e.preventDefault();
  e.stopPropagation();
  goHome();
}

function resizeRenderer(): void {
  if (!cubeRenderer) return;
  const css = cubeCssSize();
  const pr = Math.min(window.devicePixelRatio || 1, 2);
  cubeRenderer.setPixelRatio(pr);
  cubeRenderer.setSize(css, css, false);
  dom.viewCubeCanvas.style.width = css + 'px';
  dom.viewCubeCanvas.style.height = css + 'px';
}

/** Register the top-right view cube (idempotent). */
export function initViewCube(): void {
  if (registered) return;
  registered = true;

  cubeRenderer = new THREE.WebGLRenderer({
    canvas: dom.viewCubeCanvas,
    antialias: true,
    alpha: true,
    powerPreference: 'low-power'
  });
  configureRenderer(cubeRenderer);
  cubeRenderer.setClearColor(0x000000, 0);
  resizeRenderer();
  buildCube();

  onAfterRender(() => paint());
  onThemeChange(() => {
    buildCube();
    hoverPick = null;
    highlightMesh.visible = false;
    paint();
  });

  keepRenderingWhile(() => animating);
  onFrame(time => {
    if (animating) tickAnim(time);
  });

  dom.viewCubeCanvas.addEventListener('pointermove', onPointerMove);
  dom.viewCubeCanvas.addEventListener('pointerleave', onPointerLeave);
  dom.viewCubeCanvas.addEventListener('pointerdown', onPointerDown);
  dom.viewCubeHome.addEventListener('click', onHomeClick);
  window.addEventListener('resize', () => {
    resizeRenderer();
    paint();
  });
  onChromeScaleChange(() => {
    resizeRenderer();
    paint();
  });

  paint();
}
